import Vue from 'vue';
import Vuex, { Module } from 'vuex';
import { io, Socket } from 'socket.io-client';
import { IFlixcheckCheck, INotification } from 'admiral-core';
import { RootState } from '@/store';
import { SOCKET_URL } from '@/services/AdmiralApi';

Vue.use(Vuex);
export interface SocketState {
  connected: boolean;
  socket: null | Socket;
  notifications: INotification[];
  announcement: null | Date;
  flixcheckChecks: IFlixcheckCheck[];
  subscribedTopics: string[];
  topicEventHandlers: Record<string, (data: any) => void>;
}
export const socketConnection: Module<SocketState, RootState> = {
  namespaced: true,
  state: () => {
    return {
      connected: false,
      socket: null,
      notifications: [],
      announcement: null,
      flixcheckChecks: [],
      subscribedTopics: [],
      topicEventHandlers: {},
    };
  },
  getters: {
    isConnected(state: SocketState) {
      return state.connected;
    },
    newAnnouncement(state: SocketState) {
      return state.announcement;
    },
    getNotifications(state: SocketState) {
      return state.notifications;
    },
    getFlixcheckChecks(state: SocketState) {
      return state.flixcheckChecks;
    },
  },
  actions: {
    connectSocket(ctx, payload: { id: string }) {
      const protocol = process.env.NODE_ENV === 'development' ? 'ws' : 'wss';
      const socket = io(`${protocol}://${SOCKET_URL}`, {
        reconnectionDelayMax: 10000,
        query: {
          id: `${payload.id}`,
        },
        withCredentials: true,
        secure: true,
      });

      socket.on('connect', () => {
        console.log('Socket connected:', socket.id);
        ctx.commit('connected', true);

        // Resubscribe to all previously subscribed topics
        const subscribedTopics = ctx.state.subscribedTopics;
        const topicEventHandlers = ctx.state.topicEventHandlers;

        subscribedTopics.forEach((topic) => {
          socket.emit('subscribeToTopic', { topic });
        });

        // Restore all event listeners from stored handlers
        Object.keys(topicEventHandlers).forEach((key) => {
          const handler = topicEventHandlers[key];
          // Extract event name from key format: "topic:eventName" or "listener_eventName_timestamp"
          let eventName: string | null = null;

          if (key.includes(':')) {
            // Format: "topic:eventName"
            eventName = key.split(':')[1];
          } else if (key.startsWith('listener_')) {
            // Format: "listener_eventName_timestamp"
            eventName = key.split('_')[1];
          }

          if (eventName && handler) {
            socket.off(eventName); // Remove any existing listener
            socket.on(eventName, handler);
          }
        });
      });

      socket.on('notification', (notification: INotification) => {
        ctx.commit('updateNotifications', notification);
      });

      socket.on('announcement', () => {
        ctx.commit('updateAnnouncement', new Date());
      });

      socket.on('flixcheckCheckCreated', (flixcheckCheck: IFlixcheckCheck) => {
        ctx.commit('updateFlixcheckChecks', flixcheckCheck);
      });

      socket.on('disconnect', () => {
        console.log('Socket disconnected');
        ctx.commit('unsetSocket');
      });

      socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      // Set socket immediately but wait for 'connect' event to set connected state
      ctx.commit('setSocket', socket);
    },

    disconnectSocket(ctx) {
      ctx.commit('unsetSocket');
    },

    /**
     * Subscribe to a topic and optionally listen to events
     * @param ctx - Vuex context
     * @param payload - { topic: string, eventName?: string, handler?: (data: any) => void }
     */
    subscribeToTopic(
      ctx,
      payload: { topic: string; eventName?: string; handler?: (data: any) => void },
    ) {
      const { socket, connected } = ctx.state;

      if (!payload.topic) {
        console.warn('subscribeToTopic: No topic provided');
        return;
      }

      if (!socket || !connected) {
        console.warn('subscribeToTopic: Socket not connected, waiting for connection...', {
          hasSocket: !!socket,
          connected,
          topic: payload.topic,
        });

        // Wait for socket to be connected
        const checkConnection = setInterval(() => {
          const currentSocket = ctx.state.socket;
          const isConnected = ctx.state.connected;

          if (currentSocket && isConnected) {
            clearInterval(checkConnection);
            // Retry subscription once connected
            currentSocket.emit('subscribeToTopic', { topic: payload.topic });
            ctx.commit('addSubscribedTopic', payload.topic);

            // Set up event listener if provided
            if (payload.eventName && payload.handler) {
              const handlerKey = `${payload.topic}:${payload.eventName}`;
              ctx.commit('addTopicEventHandler', { key: handlerKey, handler: payload.handler });

              // Remove existing listener to avoid duplicates
              currentSocket.off(payload.eventName);

              const listenerWrapper = (data: any) => {
                payload.handler?.(data);
              };

              currentSocket.on(payload.eventName, listenerWrapper);
            }
          }
        }, 100);

        // Clear interval after 10 seconds to avoid infinite waiting
        setTimeout(() => {
          clearInterval(checkConnection);
          if (!ctx.state.socket || !ctx.state.connected) {
            console.error('subscribeToTopic: Socket connection timeout', payload.topic);
          }
        }, 10000);

        return;
      }

      // Socket is connected, proceed with subscription
      socket.emit('subscribeToTopic', { topic: payload.topic });
      ctx.commit('addSubscribedTopic', payload.topic);

      // If an event handler is provided, set up listener
      if (payload.eventName && payload.handler) {
        const handlerKey = `${payload.topic}:${payload.eventName}`;
        ctx.commit('addTopicEventHandler', { key: handlerKey, handler: payload.handler });

        // Remove existing listener for this event to avoid duplicates
        socket.off(payload.eventName);

        const listenerWrapper = (data: any) => {
          payload.handler?.(data);
        };

        socket.on(payload.eventName, listenerWrapper);
      }
    },

    /**
     * Unsubscribe from a topic and remove event handlers
     * @param ctx - Vuex context
     * @param payload - { topic: string, eventName?: string }
     */
    unsubscribeFromTopic(ctx, payload: { topic: string; eventName?: string }) {
      const { socket } = ctx.state;
      if (!socket || !payload.topic) {
        return;
      }

      // Unsubscribe from the topic on the server
      socket.emit('unsubscribeFromTopic', { topic: payload.topic });
      ctx.commit('removeSubscribedTopic', payload.topic);

      // Remove event listener if eventName is provided
      if (payload.eventName) {
        const handlerKey = `${payload.topic}:${payload.eventName}`;
        ctx.commit('removeTopicEventHandler', handlerKey);
        socket.off(payload.eventName);
      }
    },

    /**
     * Listen to a specific event on a topic (without subscribing - assumes you're already subscribed)
     * @param ctx - Vuex context
     * @param payload - { eventName: string, handler: (data: any) => void }
     */
    listenToTopicEvent(ctx, payload: { eventName: string; handler: (data: any) => void }) {
      const { socket } = ctx.state;
      if (!socket || !payload.eventName || !payload.handler) {
        return;
      }

      socket.on(payload.eventName, payload.handler);
      ctx.commit('addTopicEventListener', {
        eventName: payload.eventName,
        handler: payload.handler,
      });
    },
  },
  mutations: {
    setSocket(state: SocketState, socket: Socket) {
      state.socket = socket;
    },

    unsetSocket(state: SocketState) {
      if (state.socket) {
        // Unsubscribe from all topics before disconnecting
        state.subscribedTopics.forEach((topic) => {
          state.socket?.emit('unsubscribeFromTopic', { topic });
        });
        state.socket.disconnect();
        state.connected = false;
        state.socket = null;
        state.subscribedTopics = [];
        state.topicEventHandlers = {};
      }
    },

    updateNotifications(state: SocketState, notification: INotification) {
      state.notifications = [...state.notifications, notification];
    },

    resetNotifications(state: SocketState) {
      state.notifications = [];
    },

    connected(state: SocketState, connected: boolean) {
      state.connected = connected;
    },
    updateAnnouncement(state: SocketState, value: Date | null) {
      state.announcement = value;
    },
    updateFlixcheckChecks(state: SocketState, flixcheckCheck: IFlixcheckCheck) {
      state.flixcheckChecks = [...state.flixcheckChecks, flixcheckCheck];
    },
    resetFlixcheckChecks(state: SocketState) {
      state.flixcheckChecks = [];
    },
    addSubscribedTopic(state: SocketState, topic: string) {
      if (!state.subscribedTopics.includes(topic)) {
        state.subscribedTopics.push(topic);
      }
    },
    removeSubscribedTopic(state: SocketState, topic: string) {
      const index = state.subscribedTopics.indexOf(topic);
      if (index > -1) {
        state.subscribedTopics.splice(index, 1);
      }
    },
    addTopicEventHandler(
      state: SocketState,
      payload: { key: string; handler: (data: any) => void },
    ) {
      Vue.set(state.topicEventHandlers, payload.key, payload.handler);
    },
    removeTopicEventHandler(state: SocketState, key: string) {
      Vue.delete(state.topicEventHandlers, key);
    },
    addTopicEventListener(
      state: SocketState,
      payload: { eventName: string; handler: (data: any) => void },
    ) {
      // Store for potential cleanup later
      const key = `listener_${payload.eventName}_${Date.now()}`;
      Vue.set(state.topicEventHandlers, key, payload.handler);
    },
  },
};
